import type { List, Map, OrderedMap } from '@gonfalon/immutable-shim/immutableShim';
import type { ImmutableServerError } from '@gonfalon/legacy-http-client/httpUtils';

import { repositoryFormSelector } from '#reducers/flagCodeReferences';
import type { GetState, GlobalDispatch } from '#reducers/state';
// oxlint-disable-next-line import/no-namespace
import * as CodeReferencesAPI from '#sources/CodeReferencesAPI';
import type {
  Branch,
  CodeStatisticsForFlag,
  Extinction,
  Repository,
  RepositoryFormRecord,
} from '#utils/codeRefs/codeRefsUtils';
import type { RepositoryForm } from '#utils/codeRefs/types';
import type { GenerateActionType } from '#utils/reduxUtils';

export const collapseAll = () =>
  ({
    type: 'codeRefs/COLLAPSE_ALL',
  }) as const;

export const toggleCollapsed = (repo: string, branch: string, refIdx: number) =>
  ({
    type: 'codeRefs/TOGGLE_COLLAPSED',
    repo,
    branch,
    refIdx,
  }) as const;

export const loadMoreCodeReferencesForRepo = (repoId: string) =>
  ({
    type: 'codeRefs/LOAD_MORE_CODE_REFERENCES',
    repoId,
  }) as const;

export const resetPagination = () =>
  ({
    type: 'codeRefs/RESET_PAGINATION',
  }) as const;

export const initializeCodeReferencesRepos = (repos: OrderedMap<string, Repository>) =>
  ({
    type: 'codeRefs/INITIALIZE_REPOS',
    repos,
  }) as const;

export const initializeCodeReferencesBranch = (repoName: string, branch: Branch) =>
  ({
    type: 'codeRefs/INITIALIZE_BRANCH',
    repoName,
    branch,
  }) as const;

const requestCodeStatistics = (flagKey: string) =>
  ({
    type: 'codeRefs/REQUEST_CODE_STATISTICS',
    flagKey,
  }) as const;

const receiveCodeStatistics = (flagStatistics: OrderedMap<string, List<CodeStatisticsForFlag>>) =>
  ({
    type: 'codeRefs/RECEIVE_CODE_STATISTICS',
    flagStatistics,
  }) as const;

const requestCodeStatisticsFailed = (flagKey: string, error: ImmutableServerError) =>
  ({
    type: 'codeRefs/REQUEST_CODE_STATISTICS_FAILED',
    flagKey,
    error,
  }) as const;

const editRepositoryForm = (field: string, modifiedForm: RepositoryFormRecord) =>
  ({
    type: 'codeRefs/EDIT_REPOSITORY',
    field,
    repository: modifiedForm,
  }) as const;

export const updateRepositoryForm = (updatedRepository: Repository) =>
  ({
    type: 'codeRefs/UPDATE_REPOSITORY',
    repository: updatedRepository,
  }) as const;

export const updateRepositoryFormDone = (updatedRepository: Repository) =>
  ({
    type: 'codeRefs/UPDATE_REPOSITORY_DONE',
    repository: updatedRepository,
  }) as const;

export const updateRepositoryFormFailed = (updatedRepository: Repository, error: ImmutableServerError) =>
  ({
    type: 'codeRefs/UPDATE_REPOSITORY_FAILED',
    repository: updatedRepository,
    error,
  }) as const;

const requestCodeRefs = (flagKey: string) =>
  ({
    type: 'codeRefs/REQUEST_FLAG_CODE_REFERENCES',
    flagKey,
  }) as const;
const receiveCodeRefs = (
  repos: OrderedMap<string, Repository>,
  defaultBranchExtinctions: Map<string, List<Extinction>>,
  flagKey: string,
) =>
  ({
    type: 'codeRefs/RECEIVE_FLAG_CODE_REFERENCES',
    repos,
    defaultBranchExtinctions,
    flagKey,
  }) as const;
const requestCodeRefsFailed = (error: ImmutableServerError, flagKey: string) =>
  ({
    type: 'codeRefs/REQUEST_FLAG_CODE_REFERENCES_FAILED',
    error,
    flagKey,
  }) as const;

const requestBranchCodeRefs = (repoName: string, branchName: string, flagKey: string) =>
  ({
    type: 'codeRefs/REQUEST_BRANCH_CODE_REFERENCES',
    repoName,
    branchName,
    flagKey,
  }) as const;
const receiveBranchCodeRefs = (
  repoName: string,
  branchName: string,
  branch: Branch,
  extinctions: Map<string, Map<string, List<Extinction>>>,
  flagKey: string,
) =>
  ({
    type: 'codeRefs/RECEIVE_BRANCH_CODE_REFERENCES',
    repoName,
    branchName,
    branch,
    extinctions,
    flagKey,
  }) as const;
const requestBranchCodeRefsFailed = (
  error: ImmutableServerError,
  repoName: string,
  branchName: string,
  flagKey: string,
) =>
  ({
    type: 'codeRefs/REQUEST_BRANCH_CODE_REFERENCES_FAILED',
    error,
    repoName,
    branchName,
    flagKey,
  }) as const;

export const fetchCodeStatisticsForFlags = (projectKey: string, flagKey: string) => (dispatch: GlobalDispatch) => {
  dispatch(requestCodeStatistics(flagKey));
  return CodeReferencesAPI.getRepositoryCount(projectKey, flagKey)
    .then((flagStatistics) => {
      dispatch(receiveCodeStatistics(flagStatistics));
    })
    .catch((error: ImmutableServerError) => {
      dispatch(requestCodeStatisticsFailed(flagKey, error));
    });
};

export function editRepository(field: keyof RepositoryForm, value: boolean) {
  return (dispatch: GlobalDispatch, getState: GetState) => {
    const form = repositoryFormSelector(getState());
    const modified = form.modified.set(field, value);
    return dispatch(editRepositoryForm(field, modified));
  };
}

const FlagCodeReferencesActionCreators = {
  collapseAll,
  editRepositoryForm,
  updateRepositoryForm,
  updateRepositoryFormDone,
  updateRepositoryFormFailed,
  toggleCollapsed,
  loadMoreCodeReferencesForRepo,
  resetPagination,
  initializeCodeReferencesRepos,
  initializeCodeReferencesBranch,
  requestCodeRefs,
  receiveCodeRefs,
  requestCodeRefsFailed,
  requestBranchCodeRefs,
  receiveBranchCodeRefs,
  requestBranchCodeRefsFailed,
  requestCodeStatistics,
  receiveCodeStatistics,
  requestCodeStatisticsFailed,
};

export type FlagCodeReferencesAction = GenerateActionType<typeof FlagCodeReferencesActionCreators>;
